import { action } from "@storybook/addon-actions";

import { LangGraphBot } from "@/Apps/AITools/LangGraphBot/LangGraphBot";
import { TreeStructure } from "@phosphor-icons/react";

import { useState, useRef } from "react";

import PureCampaignBuilder from "@/Apps/Funnels/CampaignBuilder/PureCampaignBuilder";
import Button from "@/ui/Button";

import "@/Apps/Pages/Page/layout.css";

import { ASSISTANT_IDS } from "../env";
import { SwitchGroup } from "@/Apps/Pages/Properties/components/groups/SwitchGroup/SwitchGroup";
import { StatusBar } from "./StatusBar";
import { Meta } from "@storybook/react/*";
import { LANGGRAPH_DOMAIN } from "@launchos/shared-utils/env";
import { PageThumbnail } from "../Artifacts/components/campaign_builder/CampaignBuilder";
import { campaignBuilderArtifacts } from "./components/Artifacts";
import { feedbackButtons } from "./LangGraphBot.stories";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { DndProvider } from "react-dnd";

import PureCanvas from "@/Apps/Pages/V1Editor/PureCanvas";
import HTML5Backend from "react-dnd-html5-backend";
import { ISettings } from "@/Apps/Pages/Editor/types";
import { cleanThisContent } from "@/Apps/Pages/V2Editor/actions";
import { addBodyIfNone } from "../Artifacts/PageBuilderArtifact";
import { Handle, NodeProps, Position } from "reactflow";
import Node from "@/Apps/Funnels/CampaignBuilder/Node";
import slugify from "slugify";

import { getSizeFromType } from "@/Apps/Funnels/utils";
import { labelStyle } from "@/Apps/Funnels/CampaignBuilder/Node/styles";

export default {
  title: "Components/AI Tools/LangGraph Bot/Campaign Agent",
  component: LangGraphBot,
  argTypes: {
    onUpdate: { action: "updated" },
    onFinish: { action: "finished" },
  },
} as Meta;

const suggestions = [
  {
    label: `I'd like to create a marketing campaign for a new product. The product is a new savvy way to manage your finances. It's a mobile app that allows you to track your income and expenses.`,
  },
  {
    label: `Create a typical sales funnel (e.g. landing page > sales page > checkout page > thank you page)`,
  },
  {
    label: `Create a lead generation funnel for my financial consulting practice targeting small businesses`,
  },
];

export const PagePreviewNode = (props: NodeProps) => {
  const { data, sourcePosition, targetPosition, withHandles = true } = props;
  const { label, type, content } = data;

  if (type === "PageComponent" && content && content.length > 0) {
    const { width, height } = getSizeFromType(data);
    return (
      <DndProvider backend={HTML5Backend}>
        <div style={{ width, height }}>
          <div className="rounded-md overflow-hidden bg-white dark:bg-white/5 border border-black/10 dark:border-white/10 appearance-none focus:outline-none focus:ring-0 focus:border-black/10 dark:focus:border-black/10 peer">
            {withHandles && <Handle type="target" position={targetPosition || Position.Top} />}
            <PageThumbnail
              label={label || "•••"}
              content={content}
              id={slugify(label, { lower: true })}
              onClick={() => {}}
              width={width}
              height={height}
            />
            {withHandles && <Handle
              type="source"
              position={sourcePosition || Position.Bottom}
            />}
          </div>
          <div style={labelStyle}>{label}</div>
        </div>
      </DndProvider>
    );
  } else return Node(props);
};

export const CampaignBuildingArtifact = () => {
  const [campaignContent, setCampaignContent] = useState<any[]>([]);
  const campaignBuilderRef = useRef<{ fitView: () => void }>(null);
  const [showingRawContent, setShowingRawContent] = useState(false);
  const [statusBarSettings, setStatusBarSettings] = useState<any>({
    isShowing: true,
    icon: TreeStructure,
    caption: "Campaign Builder",
    actionPayload: {
      label: "Open Campaign Builder",
      onClick: () => {
        console.log("Open Campaign Builder");
      },
    },
    className: "",
    childComponent: (props: any) => (
      <div className="h-[275px] p-4 w-full">
        <PureCampaignBuilder
          showControls={false}
          showMiniMap={false}
          readOnly={true}
          {...props}
        />
      </div>
    ),
    childComponentProps: { objects: campaignContent },
  });
  const [revisedObjects, setRevisedObjects] = useState<ISettings[]>([]);
  const [artifactContainerToShow, setArtifactContainerToShow] = useState<
    "campaign_builder" | "page_builder"
  >("campaign_builder");
  const [defaultThreadId, setDefaultThreadId] = useState<string | undefined>(
    undefined
  );

  const ArtifactContainerToolbar = () => {
    return (
      <div className="flex flex-row gap-4 p-4 absolute z-10 bg-white">
        <div className="flex flex-row gap-4 justify-end">
          <div className="z-10">
            <SwitchGroup
              label={<p className="text-lg">Show Raw Content Only</p>}
              className="p-0"
              isChecked={showingRawContent}
              onClick={() => {
                setShowingRawContent(!showingRawContent);
              }}
            />
          </div>
          <Button
            // className="p-1"
            // type={ButtonTypes.SOFT}
            label="Set Default Thread ID"
            onClick={() => {
              setDefaultThreadId(
                window.prompt("Enter default thread ID") || undefined
              );
            }}
          />
        </div>
        <Button
          label="See Campaign Content"
          onClick={() => {
            console.log("campaignContent", campaignContent);
          }}
        />
        <Button
          label="Set Campaign Content"
          onClick={() => {
            const newCampaignContent = window.prompt("Enter campaign content");
            const parsedCampaignContent = JSON.parse(
              newCampaignContent || "[]"
            );
            setCampaignContent(parsedCampaignContent);
          }}
        />
        <Button
          label="Fit View"
          onClick={() => {
            campaignBuilderRef.current?.fitView();
          }}
        />
      </div>
    );
  };

  const setArtifactOptions = (options: any) => {
    console.log("setArtifactOptions called", options);
    if (options.container) {
      console.log("options", options);
      if (options.container.pageContent || options.container.campaignContent) {
        const { pageContent, campaignContent, type } = options.container;
        if (type === "campaign_builder") {
          setCampaignContent(campaignContent);
          setArtifactContainerToShow("campaign_builder");
        } else if (type === "page_builder") {
          setRevisedObjects(
            addBodyIfNone(cleanThisContent(pageContent as ISettings[]))
          );
          console.log("Set page content", pageContent);
          setArtifactContainerToShow("page_builder");
        }
      }
    }
  };

  const artifactOptions = {
    isShowing: true,
    container: {
      type: "campaign_builder" as const,
      campaignContent,
      setOptions: setArtifactOptions,
    },
  };

  const handleBeforeSubmit = async () => {
    return {
      campaignContent: campaignContent,
    };
  };

  const handleUpdate = (...args: any[]) => {
    const keys = Object.keys(args[0]);
    console.log("onUpdate keys", keys);
    console.log("onUpdate state:", args[0][keys[0]]);
    action("onUpdate")(...args);

    const { campaignContent = [] } = args[0][keys[0]] || {};
    if (campaignContent && campaignContent.length > 0) {
      console.log("onUpdate:campaignContent", campaignContent);
      setCampaignContent(campaignContent);
    }
  };

  const handleFinish = (...args: any[]) => {
    // thread
    console.log("onFinish args:", args);
    console.log("onFinish state:", args[0]?.values);
    const { campaignContent } =
      (args[0]?.values as { campaignContent: any[] }) || {};
    if (campaignContent && campaignContent.length > 0) {
      setCampaignContent(campaignContent);
      console.log("campaignContent", campaignContent);

      setStatusBarSettings({
        ...statusBarSettings,
        // isExpanded: true,
        className: "bg-yellow-500",
        childComponentProps: {
          objects: campaignContent,
          ref: campaignBuilderRef,
        },
      });

      // Fit the campaign view to show all nodes
      setTimeout(() => {
        campaignBuilderRef.current?.fitView();
      }, 100);
    }
    action("onFinish")(...args);
  };

  const handleLoad = (...args: any[]) => {
    // thread.values
    console.log("onLoad args:", args);
    const { campaignContent } = args[0] as unknown as {
      campaignContent: any[];
    };
    console.log("campaignContent", campaignContent);
    if (campaignContent && campaignContent.length > 0) {
      setCampaignContent(campaignContent);
      setStatusBarSettings({
        ...statusBarSettings,
        // isExpanded: true,
        childComponentProps: {
          objects: campaignContent,
          ref: campaignBuilderRef,
        },
      });
    }
  };

  const handleCampaignBuilderChange = ({
    changes,
    nodes,
  }: {
    changes: any;
    nodes: any;
  }) => {
    console.log("changes", changes);
    console.log("nodes", nodes);
  };

  const handleCampaignBuilderUpdateObject = (...args: any[]) => {
    console.log("updateObject args:", args);
  };

  return (
    <div className="h-full w-full">
      <div className="h-[95vh] w-full flex flex-row gap-4 fixed">
        <div className="w-1/3 h-full">
          <LangGraphBot
            apiUrl={LANGGRAPH_DOMAIN}
            assistantId={ASSISTANT_IDS.CAMPAIGN}
            defaultThreadId={defaultThreadId}
            title="What can I help you launch today?"
            subtitle="Plan, manage, and launch marketing campaigns, funnels, and campaign assets with ease!"
            placeholder="Ask Launch OS a question..."
            artifacts={campaignBuilderArtifacts}
            artifactOptions={artifactOptions}
            setArtifactOptions={setArtifactOptions}
            withFeedbackButtons={false}
            feedbackButtons={feedbackButtons}
            suggestions={suggestions}
            // sessionReplay
            sessionReplaySettings={{
              speed: 500,
              countdown: 3,
            }}
            statusBarComponent={StatusBar}
            statusBarSettings={statusBarSettings}
            onBeforeSubmit={handleBeforeSubmit}
            onUpdate={handleUpdate}
            onFinish={handleFinish}
            onLoad={handleLoad}
            showingRawContent={showingRawContent}
          />
        </div>
        <div className="flex-1 border rounded-lg border-black/10 h-full overflow-y-auto min-w-0">
          {artifactContainerToShow === "campaign_builder" && (
            <DndProvider backend={HTML5Backend}>
              <ArtifactContainerToolbar />
              <PureCampaignBuilder
                ref={campaignBuilderRef}
                id="campaign-builder"
                objects={campaignContent}
                onChange={handleCampaignBuilderChange}
                deleteConnection={() => {}}
                updateObject={handleCampaignBuilderUpdateObject}
                deleteObject={() => {}}
                CustomNode={PagePreviewNode}
                createConnection={() => {}}
                createObject={() => {}}
              />
            </DndProvider>
          )}

          {artifactContainerToShow === "page_builder" && (
            <div className="h-full w-full">
              <DndProvider backend={HTML5Backend}>
                <PureCanvas
                  content={revisedObjects}
                  onSave={() => null}
                  onItemSelect={() => null}
                  onChange={() => null}
                  disableDefaultProperties
                  setActiveAddNewTab={() => null}
                  mobileState={{ type: MobileState.FULLSCREEN }}
                  setMobileState={() => ({ type: MobileState.FULLSCREEN })}
                  onLoad={() => null}
                />
              </DndProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
